(ns app.front.view.group.admin.inspections.checklists.subs
  (:require [app.domain.group.model :as group-model]
            [app.domain.inspection.model :as inspection-model]
            [app.front.entities :as entities]
            [app.front.ui.dnd :as dnd]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.inspections.util :as inspection-util]
            [app.i18n.model :as i18n-model]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

;; Checklist templates are under a given inspection template kind (= inspection type)
(rf/reg-sub ::selected-template-kind-id :-> ::selected-template-kind-id)
(rf/reg-sub ::selected-checklist-template-id :-> ::selected-checklist-template-id)

(rf/reg-sub ::reordering?
  (fn [db _]
    (-> db ::reordering? boolean)))

(rf/reg-sub ::reordering-checklist-templates?
  (fn [db _]
    (-> db ::reordering-checklist-templates? boolean)))

(rf/reg-sub ::selected-checklist-template
  :<- [::selected-checklist-template-id]
  (fn [checklist-id _]
    (when checklist-id
      (<sub [::entities/entity checklist-id]))))

(rf/reg-sub ::default-checklist-template?
  :<- [::selected-checklist-template]
  :-> :inspection.template.kind/_default-checklist-templates)

(rf/reg-sub ::inspection-template-kind-items
  :<- [::gas/template-kind-entities]
  :<- [::selected-template-kind-id]
  :<- [:language]
  (fn [[kinds selected-id language] _]
    (map (fn [{id :db/id :as kind}]
           {:id        id
            :selected? (= id selected-id)
            :title     (inspection-util/template-kind->name kind language)})
         kinds)))

(rf/reg-sub ::inspection-checklist-templates
  :<- [::gas/entities-for-group]
  :<- [::selected-template-kind-id]
  (fn [[group-entities kind-id]]
    (->> group-entities
         (filter #(and (inspection-model/is-inspection-checklist-template? %)
                       (-> % :entity/parent :db/id (= kind-id))))
         (sort-by :ordered/order))))

(rf/reg-sub ::copy-modal-open?
  :-> ::copy-modal-open?)

(rf/reg-sub ::selected-source-template-kind-id
  :-> ::selected-source-template-kind-id)

(rf/reg-sub ::selected-checklist-ids-to-copy
  :-> ::selected-checklist-ids-to-copy)

(rf/reg-sub ::detected-conflicts
  :-> ::detected-conflicts)

(rf/reg-sub ::selected-conflict-resolution-strategy
  :-> ::selected-conflict-resolution-strategy)

(rf/reg-sub ::available-source-template-kinds
  :<- [::gas/template-kind-entities]
  :<- [::selected-template-kind-id]
  (fn [[kinds current-kind-id] _]
    (->> kinds
         (filter #(not= (:db/id %) current-kind-id))
         (filter #(seq (inspection-model/checklist-templates %))))))

(rf/reg-sub ::source-checklists
  :<- [::gas/entities-for-group]
  :<- [::selected-source-template-kind-id]
  (fn [[group-entities source-kind-id]]
    (when source-kind-id
      (->> group-entities
           (filter #(and (inspection-model/is-inspection-checklist-template? %)
                         (-> % :entity/parent :db/id (= source-kind-id))))
           (sort-by :ordered/order)))))

(rf/reg-sub ::checklist-configuration-v2
  :<- [::ff/enabled? :feature-flag.id/checklist-configuration-v2]
  :<- [::ff/enabled? :feature-flag.id/checklist-configuration-v2-mobile]
  :<- [::gas/has-feature? :group.feature/mobile-app]
  group-model/has-mobile-feature?)


;;; Events
;;;;;;;;;;

(rf/reg-event-db ::select-template-kind
  (fn [db [_ kind-id]]
    (-> db
        (assoc ::selected-template-kind-id kind-id)
        (dissoc ::selected-checklist-template-id)
        (dissoc ::reordering-checklist-templates?))))

(rf/reg-event-db ::select-checklist-template
  (fn [db [_ checklist-id]]
    (assoc db ::selected-checklist-template-id checklist-id)))

(rf/reg-event-db ::unselect-checklist-template
  (fn [db _]
    (dissoc db ::selected-checklist-template-id)))

(rf/reg-event-db ::toggle-reordering
  (fn [db _]
    (update db ::reordering? not)))

(rf/reg-event-db ::toggle-reordering-checklist-templates
  (fn [db _]
    (update db ::reordering-checklist-templates? not)))

(rf/reg-event-fx ::create-checklist-template
  (fn [_ [_ kind-id language]]
    {:comm/command {:command    :inspection/create-checklist-template
                    :data       {:template-kind-id kind-id
                                 :language         language}
                    :on-success [::create-checklist-template.success]}}))

(rf/reg-event-fx ::create-checklist-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::select-checklist-template
                   (entities/id-of-some-in-apply-changes :entity.type/inspection-checklist-template response)]
                  [:notifications/add :toast :success :success :inspection.checklist/created]]}))

(rf/reg-event-fx ::delete-checklist-template
  (fn [{:keys [db]} _]
    {:comm/command {:command    :inspection/delete-checklist-template
                    :data       {:id (::selected-checklist-template-id db)}
                    :on-success [::delete-checklist-template.success]}}))

(rf/reg-event-fx ::delete-checklist-template.success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::unselect-checklist-template]
                  [:notifications/add :toast :success :success :inspection.checklist/deleted]]}))

(rf/reg-event-fx ::set-default-checklist-template
  (fn [{:keys [db]} [_ add?]]
    {:comm/command {:command    :inspection/set-default-checklist
                    :data       {:id   (::selected-checklist-template-id db)
                                 :add? add?}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :inspection.checklist/set-default-done}}}))

(rf/reg-event-fx ::reorder-checklist-templates
  (fn [_ [_ from to]]
    {:comm/command {:command    :inspection/reorder-checklist-templates
                    :data       {:templates (dnd/ordering-tx-for-entity from to)}
                    :on-success [::entities/apply-changes]}}))

(rf/reg-event-db ::open-copy-modal
  (fn [db _]
    (assoc db ::copy-modal-open? true)))

(rf/reg-event-db ::close-copy-modal
  (fn [db _]
    (-> db
        (dissoc ::copy-modal-open?)
        (dissoc ::selected-source-template-kind-id)
        (dissoc ::selected-checklist-ids-to-copy)
        (dissoc ::detected-conflicts)
        (dissoc ::selected-conflict-resolution-strategy))))

(rf/reg-event-db ::select-conflict-resolution-strategy
  (fn [db [_ strategy]]
    (assoc db ::selected-conflict-resolution-strategy strategy)))

(rf/reg-event-db ::select-source-template-kind
  (fn [db [_ kind-id]]
    (-> db
        (assoc ::selected-source-template-kind-id kind-id)
        (dissoc ::selected-checklist-ids-to-copy))))

(rf/reg-event-db ::toggle-checklist-selection
  (fn [db [_ checklist-id]]
    (update db ::selected-checklist-ids-to-copy
            (fn [selected]
              (let [selected-set (set selected)]
                (if (contains? selected-set checklist-id)
                  (vec (remove #{checklist-id} selected))
                  (conj (or selected []) checklist-id)))))))

(rf/reg-event-db ::select-all-checklists
  (fn [db [_ checklist-ids]]
    (assoc db ::selected-checklist-ids-to-copy checklist-ids)))

(rf/reg-event-db ::deselect-all-checklists
  (fn [db _]
    (assoc db ::selected-checklist-ids-to-copy [])))

(rf/reg-event-fx ::check-conflicts-and-copy
  (fn [{:keys [db]} [_ selected-source-items target-checklists language]]
    (let [get-name     (fn [checklist]
                         (i18n-model/get-text (:inspection.checklist/names checklist) language))
          target-names (set (map get-name target-checklists))
          conflicts    (filter (fn [checklist]
                                 (contains? target-names (get-name checklist)))
                               selected-source-items)]
      (if (seq conflicts)
        {:db (-> db
                 (assoc ::detected-conflicts conflicts)
                 (assoc ::selected-conflict-resolution-strategy :append-suffix)
                 (dissoc ::copy-modal-open?))}
        {:dispatch [::execute-copy :append-suffix]}))))

(rf/reg-event-fx ::execute-copy
  (fn [{:keys [db]} [_ conflict-resolution]]
    (let [source-kind-id (::selected-source-template-kind-id db)
          target-kind-id (::selected-template-kind-id db)
          checklist-ids  (::selected-checklist-ids-to-copy db)]
      {:comm/command {:command    :inspection/copy-checklist-templates
                      :data       {:source-template-kind-id source-kind-id
                                   :target-template-kind-id target-kind-id
                                   :checklist-template-ids  checklist-ids
                                   :conflict-resolution     conflict-resolution}
                      :on-success [::copy-success]}})))

(rf/reg-event-fx ::copy-success
  (fn [_ [_ status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::close-copy-modal]
                  [:notifications/add :toast :success :success :inspection.checklist/copied]]}))

(rf/reg-event-db ::open-export-modal
  (fn [db _]
    (assoc db ::export-modal-open? true)))

(rf/reg-event-db ::close-export-modal
  (fn [db _]
    (-> db
        (dissoc ::export-modal-open?)
        (dissoc ::selected-checklist-ids-to-export))))

(rf/reg-sub ::export-modal-open?
  :-> ::export-modal-open?)

(rf/reg-sub ::selected-checklist-ids-to-export
  :-> ::selected-checklist-ids-to-export)

(rf/reg-event-db ::toggle-export-checklist-selection
  (rf/path [::selected-checklist-ids-to-export])
  (fn [checklists [_ id]]
    (util/toggle-in-set checklists id)))

(rf/reg-event-db ::select-all-export-checklists
  (fn [db [_ checklist-ids]]
    (assoc db ::selected-checklist-ids-to-export (set checklist-ids))))

(rf/reg-event-db ::deselect-all-export-checklists
  (fn [db _]
    (assoc db ::selected-checklist-ids-to-export #{})))

(rf/reg-sub ::import-conflict-state
  :-> ::import-conflict-state)

(rf/reg-sub ::selected-import-conflict-resolution-strategy
  :-> ::selected-import-conflict-resolution-strategy)

(rf/reg-event-db ::show-import-conflict-dialog
  (fn [db [_ state]]
    (-> db
        (assoc ::import-conflict-state state)
        (assoc ::selected-import-conflict-resolution-strategy :append-suffix))))

(rf/reg-event-db ::close-import-conflict-dialog
  (fn [db _]
    (-> db
        (dissoc ::import-conflict-state)
        (dissoc ::selected-import-conflict-resolution-strategy))))

(rf/reg-event-db ::select-import-conflict-resolution-strategy
  (fn [db [_ strategy]]
    (assoc db ::selected-import-conflict-resolution-strategy strategy)))
